import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { Flex } from 'reflexbox';
import styled from 'styled-components';

import * as graphql from '@treams/treams-network';
import { ITimeMachine, LineChart, Skeleton, tokens } from '@treams/treams-ui';

const AnalyticsGraphMetricEmpty = styled(Flex)`
	height: 120px;
	background-color: ${tokens.surface.primary};
	color: ${tokens.text.secondary};
	align-items: center;
	justify-content: center;
	border-radius: ${tokens.radius.sm}px;
`;

export function AnalyticsGraphMetric(props: {
	metricGroupID: string;
	metric: graphql.AnalyticsGraphMetric;
	timeMachine: ITimeMachine;
}) {
	const { t } = useTranslation();

	const { datapoints } = props.metric;

	if (datapoints.length < 2 || datapoints.every((datapoint) => datapoint.value === 0)) {
		return (
			<AnalyticsGraphMetricEmpty>{t('analytics.metrics.graph.empty')}</AnalyticsGraphMetricEmpty>
		);
	}

	const boundedDatapoints = datapoints.map((datapoint, index) => {
		// Force to use the lower bound of the time machine.
		if (index === 0) {
			return {
				...datapoint,
				timestamp: moment(props.timeMachine.current[0]).toDate(),
			};
		}

		// Force to use the upper bound of the time machine.
		if (index === datapoints.length - 1) {
			return {
				...datapoint,
				timestamp: moment(props.timeMachine.current[1]).toDate(),
			};
		}

		return datapoint;
	});

	return (
		<LineChart
			data={boundedDatapoints.map((datapoint) => datapoint.value) as number[]}
			labels={boundedDatapoints.map((datapoint) => moment(datapoint.timestamp).format('LL'))}
			bounds={
				typeof props.metric.min === 'number' && typeof props.metric.max === 'number'
					? [props.metric.min, props.metric.max]
					: undefined
			}
			compactLayout
			showBackground
		/>
	);
}

AnalyticsGraphMetric.Skeleton = styled(Skeleton.Box)`
	height: 120px;
	width: 100%;
`;
